import type { UpdateLabProfileInput } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { TextField } from '#client/components/ui/TextField';
import { LogoField } from '#client/features/print';
import { letterheadPreview } from './company-form.logic';
import type { SetField } from '#client/hooks/useFormValues';

interface FieldsProps {
  values: UpdateLabProfileInput;
  onFieldChange: SetField<UpdateLabProfileInput>;
  errors: Record<string, string>;
}

export function CompanyIdentityCard({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <Card title="Identity">
      <div className="c-form-grid">
        <TextField
          label="Lab name"
          value={values.name}
          error={errors.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          required
        />
        <TextField
          label="Tagline"
          value={values.tagline ?? ''}
          error={errors.tagline}
          onChange={(event) => onFieldChange('tagline', event.target.value)}
          helpText="e.g. Accurate diagnostics, better outcomes"
        />
      </div>
    </Card>
  );
}

/** Address, phones and web, with the letterhead they compose underneath. */
export function CompanyContactCard({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <Card title="Address and contact">
      <div className="c-form-grid">
        <TextField
          label="Address line 1"
          value={values.address_line1 ?? ''}
          error={errors.address_line1}
          onChange={(event) => onFieldChange('address_line1', event.target.value)}
        />
        <TextField
          label="Address line 2"
          value={values.address_line2 ?? ''}
          error={errors.address_line2}
          onChange={(event) => onFieldChange('address_line2', event.target.value)}
        />
        <TextField
          label="City"
          value={values.city ?? ''}
          error={errors.city}
          onChange={(event) => onFieldChange('city', event.target.value)}
        />
        <TextField
          label="Phone"
          value={values.phone_primary ?? ''}
          error={errors.phone_primary}
          onChange={(event) => onFieldChange('phone_primary', event.target.value)}
        />
        <TextField
          label="Second phone"
          value={values.phone_secondary ?? ''}
          error={errors.phone_secondary}
          onChange={(event) => onFieldChange('phone_secondary', event.target.value)}
        />
        <TextField
          label="Email"
          type="email"
          value={values.email ?? ''}
          error={errors.email}
          onChange={(event) => onFieldChange('email', event.target.value)}
        />
        <TextField
          label="Website"
          value={values.website ?? ''}
          error={errors.website}
          onChange={(event) => onFieldChange('website', event.target.value)}
        />
      </div>

      <dl className="c-summary">
        <div className="c-summary__row">
          <dt>Letterhead preview</dt>
          <dd className="text-fg-muted">{letterheadPreview(values)}</dd>
        </div>
      </dl>
    </Card>
  );
}

export function CompanyRegistrationCard({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <Card title="Registration">
      <div className="c-form-grid">
        <TextField
          label="Licence number"
          value={values.license_no ?? ''}
          error={errors.license_no}
          onChange={(event) => onFieldChange('license_no', event.target.value)}
          helpText="Printed on official documents"
        />
        <TextField
          label="NTN"
          value={values.ntn_no ?? ''}
          error={errors.ntn_no}
          onChange={(event) => onFieldChange('ntn_no', event.target.value)}
        />
      </div>
    </Card>
  );
}

interface MarkCardProps {
  logoDataUrl: string | null | undefined;
  onLogoChange: (logoDataUrl: string | null) => void;
}

export function CompanyMarkCard({ logoDataUrl, onLogoChange }: MarkCardProps) {
  return (
    <Card title="Mark" subtitle="Used on the rate list, slips and procedures">
      <LogoField
        logo={{
          enabled: true,
          data_url: logoDataUrl ?? null,
          width: 30,
          height: 26,
        }}
        onChange={(patch) => onLogoChange(patch.data_url ?? null)}
      />
    </Card>
  );
}
